import { Component, inject, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { catchError, of, switchMap, tap } from "rxjs";
import { environment } from "../../environments/environment";
import { CtaService } from "../services/cta.service";
import { CurrencyService } from "../services/currency.service";
import { DataService } from "../services/data.service";
import { OrderService } from "../services/order.service";
import { PaymentValidationService } from "../services/payment-validation.service";
import { CartService } from "../services/cart.service";
import { Transaction } from "../models/payment.model";
import { PaymentMode } from "../shared/enums";

@Component({
  selector: "app-bill-paid",
  templateUrl: "./bill-paid.component.html",
  styleUrls: ["./bill-paid.component.scss"],
  standalone: false,
})
export class BillPaidComponent implements OnInit {
  public transaction: Transaction;
  public total: number;
  public subtotal: number;
  public ctaService = inject(CtaService);
  private router = inject(Router);
  private orderService = inject(OrderService);
  private paymentValidation = inject(PaymentValidationService);
  private currencyService = inject(CurrencyService);
  private dataService = inject(DataService);
  private cartService = inject(CartService);

  ngOnInit(): void {
    const hasQuery = !!window.location.search;

    if (!hasQuery) {
      //  Google Pay / in-app redirect (no query string)
      const lastTx = this.orderService.getLastTransaction();
      if (!lastTx) {
        this.close();
        return;
      }
      this.applyTransaction(lastTx);
      return;
    }

    //  NI / other gateways with query in URL
    this.paymentValidation
      .setTableAndRestaurant(window.location.search)
      .pipe(
        tap((response) => {
          this.dataService.table.set(response.table);
          this.dataService.restaurant.set(response.restaurant);
        }),
        switchMap(() =>
          this.paymentValidation
            .validateNI(window.location.search)
            .pipe(catchError(() => of(null)))
        ),
        switchMap((txFromNI) => {
          if (txFromNI) {
            this.applyTransaction(txFromNI);
            return of(null);
          }
          return this.paymentValidation
            .validatePayment$(window.location.search)
            .pipe(catchError(() => of(null)));
        })
      )
      .subscribe({
        next: (txFromPayment) => {
          if (txFromPayment) {
            this.applyTransaction(txFromPayment);
          } else if (!this.transaction) {
            const lastTx = this.orderService.getLastTransaction();
            if (!lastTx) {
              this.close(false);
              return;
            }
            this.applyTransaction(lastTx);
          }
        },
        error: (err) => {
          console.error('Validation failed:', err);
          this.close();
        },
      });
  }

  private applyTransaction(tx: Transaction | null | undefined): void {
    if (!tx) {
      this.close();
      return;
    }
    this.transaction = tx;
    this.dataService.transactionLoaded.set(true);
    // Not at the top of ngOnInit — this page is reached on failed redirects too.
    this.cartService.clearCart();

    if (tx.cheque?.cheque_id) {
      this.total = Number(tx.cheque?.amount ?? 0);
      this.subtotal = Number(tx.cheque?.original_amount ?? 0);
    } else if (tx.orders?.length) {
      this.total = tx.orders.reduce((acc, o) => acc + (o?.total_price ?? 0), 0);
      this.subtotal = tx.orders.reduce((acc, o) => acc + (o?.total_price_without_discount ?? 0), 0);
    }

    this.initializeCTA();
  }

  public initializeCTA(): void {
    if (!this.transaction?.orders?.length) {
      return;
    }
    this.ctaService.cta.set({
      // Transaction orders can come without a CTA - the old page guarded this too.
      url: this.transaction.orders[0].cta?.url ?? '',
      text: this.transaction.orders[0].cta?.text ?? '',
      total_price: this.total,
    });
  }

  public openCTA() {
    this.ctaService.openCTA();
  }

  public close(navigate: boolean = true): void {
    this.dataService.transactionLoaded.set(true);
    if (navigate) {
      this.router.navigate([""]);
    }
  }

  public get chequeNumber(): string {
    return this.transaction?.orders?.[0]?.cheque?.number ?? '';
  }

  public openReceipt(): void {
    const transactionId = this.transaction?.transaction?.id;
    if (!transactionId) {
      return;
    }

    const isPayAtTable =
      this.dataService.restaurant().payment_mode === PaymentMode.PayAtTable;
    const receiptPath = isPayAtTable ? "cheque" : "transaction";
    const url = `${environment.urls.api}/receipt/${receiptPath}/${transactionId}.pdf`;
    window.open(url, "_blank");
  }

}
